/**
 * Modal Component Registry
 *
 * Maps modal slugs (the first segment of the `what` string passed to modal())
 * to lazy-loaded Preact components. When a slug has a registered component,
 * modal() will mount the Preact tree instead of fetching PHP HTML.
 *
 * To migrate a modal:
 *   1. Create a .tsx component in src/features/modals/
 *   2. Register it here with its slug
 *   3. The PHP modal file can stay — it just won't be called any more
 *
 * Each component receives ModalComponentProps:
 *   - parts: the colon-split segments of the `what` string
 *   - onClose: call to close this modal
 *   - onRefresh: call to re-render this modal (swap)
 */
import type { ComponentType } from "preact";
import type { ModalKey } from "@brave-new-world/shared/viewContract";

export interface ModalComponentProps {
  /** The colon-split segments of the modal `what` string, e.g. ["editlist", "abc123", ""] */
  parts: string[];
  /** Close the current (topmost) modal */
  onClose: () => void;
  /** Refresh/re-render this modal */
  onRefresh: () => void;
}

type LazyLoader = () => Promise<{ default: ComponentType<ModalComponentProps> }>;

const registry = new Map<string, LazyLoader>();

/**
 * Register a Preact modal component for a given slug.
 * The loader should dynamically import the component module and return its default export.
 */
export function registerModal(slug: ModalKey, loader: LazyLoader): void {
  registry.set(slug, loader);
}

/**
 * Look up a registered Preact component for a modal slug.
 * Returns undefined if no component is registered (falls back to PHP).
 */
export function getModalComponent(slug: string): LazyLoader | undefined {
  return registry.get(slug);
}

// ── Register modal components here ───────────────────────────────

// Reference implementations
registerModal("contact", () => import("../features/modals/ContactModal"));
registerModal("editlist", () => import("../features/modals/EditListModal"));

// Pure static / confirmation modals
registerModal("notforpictufy", () => import("../features/modals/NotForPictufyModal"));
registerModal("image-request-sent", () => import("../features/modals/BriefImageRequestSentModal"));
registerModal("duplicates", () => import("../features/modals/DuplicatesModal"));
registerModal("resetpasswordsuccess", () => import("../features/modals/ResetPasswordSuccessModal"));
registerModal("expanded-products-no", () => import("../features/modals/ExpandedProductsNoModal"));
registerModal("expanded-products-yes", () => import("../features/modals/ExpandedProductsYesModal"));
registerModal("mockup-generate", () => import("../features/modals/MockupGenerateModal"));

// Simple form modals
registerModal("addgroup", () => import("../features/modals/AddGroupModal"));
registerModal("addgroupartist", () => import("../features/modals/AddGroupArtistModal"));
registerModal("multiaddtag", () => import("../features/modals/MultiAddTagModal"));
registerModal("multideletetag", () => import("../features/modals/MultiDeleteTagModal"));
registerModal("newcollcat", () => import("../features/modals/NewCollCatModal"));
registerModal("addcollection", () => import("../features/modals/AddCollectionModal"));
registerModal("setcustomdl", () => import("../features/modals/SetCustomDlModal"));
registerModal("addplugin", () => import("../features/modals/AddPluginModal"));
registerModal("metric", () => import("../features/modals/MetricModal"));

// Auth modals
registerModal("login", () => import("../features/modals/LoginModal"));
registerModal("forgotpassword", () => import("../features/modals/ForgotPasswordModal"));
registerModal("changepassword", () => import("../features/modals/ChangePasswordModal"));
registerModal("resetpassword", () => import("../features/modals/ResetPasswordModal"));
registerModal("setpassword", () => import("../features/modals/SetPasswordModal"));

// Content / info page modals
registerModal("biography-create", () => import("../features/modals/BiographyCreateModal"));
registerModal("artistapplication", () => import("../features/modals/ArtistApplicationModal"));
registerModal("downloadernotice", () => import("../features/modals/BrowserDownloaderNoticeModal"));
registerModal("expiredreminders", () => import("../features/modals/ExpiredRemindersModal"));
registerModal("invoicereminders", () => import("../features/modals/InvoiceRemindersModal"));
registerModal("signup-artist", () => import("../features/modals/SignupArtistModal"));
registerModal("profilepic", () => import("../features/modals/ProfilePicModal"));

// Complex form modals
registerModal("firstquestions", () => import("../features/modals/FirstQuestionsModal"));
registerModal("customizetransfer", () => import("../features/modals/CustomizeTransferModal"));

// ── Data-driven modals (batch 3) ──────────────────────────────────

// Image viewers
registerModal("expviewer", () => import("../features/modals/ExpirationViewerModal"));
registerModal("expnotice", () => import("../features/modals/ExpirationNoticeModal"));
registerModal("expconfirm", () => import("../features/modals/ExpirationConfirmModal"));
registerModal("pre_view", () => import("../features/modals/PreViewModal"));
registerModal("showinterior", () => import("../features/modals/ShowInteriorModal"));

// List / table displays
registerModal("pages", () => import("../features/modals/PagesModal"));
registerModal("blockwords", () => import("../features/modals/BlockwordsModal"));
registerModal("blockwords-ai", () => import("../features/modals/BlockwordsModal"));
registerModal("artworkcollections", () => import("../features/modals/ArtworkCollectionsModal"));
registerModal("invoices", () => import("../features/modals/InvoicesModal"));
registerModal("artistfeatures", () => import("../features/modals/ArtistFeaturesModal"));
registerModal("newsletters", () => import("../features/modals/NewslettersModal"));
registerModal("newslettersend", () => import("../features/modals/NewsletterSendModal"));

// Edit forms
registerModal("editcollcat", () => import("../features/modals/EditCollCatModal"));
registerModal("editpage", () => import("../features/modals/EditPageModal"));
registerModal("addadvance", () => import("../features/modals/AddAdvanceModal"));
registerModal("addvariant", () => import("../features/modals/AddVariantModal"));

// Image pickers / grids
registerModal("setthumb", () => import("../features/modals/SetThumbModal"));
registerModal("collectionthumb", () => import("../features/modals/CollectionThumbModal"));
registerModal(
  "expanded-products-select",
  () => import("../features/modals/ExpandedProductsSelectModal")
);
registerModal("addfeaturedartist", () => import("../features/modals/AddFeaturedArtistModal"));

// Feature / group management
registerModal("featureartist", () => import("../features/modals/FeatureArtistModal"));
registerModal("addtopartnergroups", () => import("../features/modals/ResellerGroupsModal"));
registerModal("partnergroups", () => import("../features/modals/ManageResellerGroupsModal"));
registerModal("reorderinteriors", () => import("../features/modals/ReorderMockupsModal"));
registerModal("adduser", () => import("../features/modals/AddUserModal"));

// Static modals (batch 3)
registerModal("uploadrules", () => import("../features/modals/UploadRulesModal"));
registerModal("mockupwelcome", () => import("../features/modals/MockupWelcomeModal"));
registerModal("newversion", () => import("../features/modals/NewVersionModal"));
registerModal("withselected", () => import("../features/modals/WithSelectedModal"));
registerModal("expanded-products", () => import("../features/modals/ExpandedProductsOptInModal"));

// ── Data-driven modals (batch 4) ──────────────────────────────────

// Newsletter creation
registerModal("sendnewsletter", () => import("../features/modals/SendNewsletterModal"));
registerModal(
  "sendnewsletter-artist",
  () => import("../features/modals/SendNewsletterArtistModal")
);

// Toggle-list modals
registerModal("addlist", () => import("../features/modals/AddListModal"));
registerModal("multilist", () => import("../features/modals/MultiListModal"));
registerModal("linkcollection", () => import("../features/modals/LinkCollectionModal"));
registerModal("briefcollection", () => import("../features/modals/BriefCollectionModal"));
registerModal("selectresellers", () => import("../features/modals/SelectResellersModal"));
registerModal(
  "selectpartnersforgroup",
  () => import("../features/modals/SelectResellersForGroupModal")
);
registerModal("selectrecipients", () => import("../features/modals/SelectRecipientsModal"));
registerModal(
  "selectrecipients-artist",
  () => import("../features/modals/SelectRecipientsArtistModal")
);

// Reorder / edit
registerModal("reorderlist", () => import("../features/modals/ReorderListModal"));

// E-commerce / store
registerModal("marketing", () => import("../features/modals/MarketingModal"));
registerModal("marketingmaterial", () => import("../features/modals/MarketingMaterialModal"));
registerModal("marketingprompt", () => import("../features/modals/MarketingPromptModal"));

// Static / form modals (batch 4)
registerModal("savesearch", () => import("../features/modals/SaveSearchModal"));
registerModal("collwizard", () => import("../features/modals/CollWizardModal"));
registerModal("wizardresellers", () => import("../features/modals/WizardResellersModal"));

// ── Data-driven modals (batch 5) ──────────────────────────────────

// Collection management
registerModal("addtocollection", () => import("../features/modals/AddToCollectionModal"));
registerModal("multicollection", () => import("../features/modals/MultiCollectionModal"));
registerModal("editcollection", () => import("../features/modals/EditCollectionModal"));
registerModal("collectiondetails", () => import("../features/modals/CollectionDetailsModal"));
registerModal("partnercollection", () => import("../features/modals/ResellerCollectionModal"));

// User management
registerModal(
  "blockuserfrompartners",
  () => import("../features/modals/BlockUserFromResellersModal")
);
registerModal("managefollowed", () => import("../features/modals/ManageFollowedModal"));

// E-commerce
registerModal("editvariant", () => import("../features/modals/EditVariantModal"));
registerModal("yourphotos", () => import("../features/modals/YourPhotosModal"));
registerModal("reupload", () => import("../features/modals/ReuploadModal"));
registerModal("bulksetvalues", () => import("../features/modals/BulkSetValuesModal"));
registerModal("uploadinterioruser", () => import("../features/modals/UploadUserMockupModal"));

// Portfolio review
registerModal("portfolioreviewguide", () => import("../features/modals/PortfolioReviewGuideModal"));

// Language / auth
registerModal("selectlang", () => import("../features/modals/SelectLangModal"));
registerModal("edituser", () => import("../features/modals/EditUserModal"));
registerModal("resetpw", () => import("../features/modals/ResetPwModal"));
registerModal("signup", () => import("../features/modals/SignupModal"));

// Artist
registerModal("biography", () => import("../features/modals/BiographyModal"));
registerModal("biography-article", () => import("../features/modals/BiographyArticleModal"));

// Admin / briefs
registerModal("admin", () => import("../features/modals/AdminModal"));
registerModal("editbrief", () => import("../features/modals/EditBriefModal"));
registerModal("briefs", () => import("../features/modals/BriefsModal"));

// ── Data-driven modals (batch 6) ──────────────────────────────────

// A bare image, shown large — task attachments and anything else that is just a picture.
registerModal("image", () => import("../features/modals/ImageModal"));

// Interior & mockup modals
registerModal("addkeywordinterior", () => import("../features/modals/AddKeywordMockupModal"));
registerModal("mockup-select", () => import("../features/modals/MockupSelectModal"));
registerModal("previewinterior", () => import("../features/modals/PreviewMockupModal"));
registerModal("previewinterior2", () => import("../features/modals/PreviewMockup2Modal"));
registerModal("selectinterior", () => import("../features/modals/SelectMockupModal"));
registerModal("selectinterior2", () => import("../features/modals/SelectMockup2Modal"));
registerModal("defaultinterior", () => import("../features/modals/DefaultMockupModal"));
registerModal("mockupsall", () => import("../features/modals/MockupsAllModal"));
registerModal("editinterior", () => import("../features/modals/EditMockupModal"));
registerModal("editinterioruser", () => import("../features/modals/EditUserMockupModal"));
registerModal("ieclient", () => import("../features/modals/IeclientModal"));
registerModal("ieclient-select", () => import("../features/modals/IeclientSelectModal"));

// Artwork modals
registerModal("view", () => import("../features/modals/ViewModal"));
registerModal(
  "incomingartworkchecker",
  () => import("../features/modals/IncomingArtworkCheckerModal")
);
registerModal("details", () => import("../features/modals/DetailsModal"));
registerModal("editphoto", () => import("../features/modals/EditPhotoModal"));
registerModal("exclusivity", () => import("../features/modals/ArtworkExclusivityModal"));
registerModal("alternative", () => import("../features/modals/AlternativeModal"));

// E-commerce modals
registerModal("ecommerce", () => import("../features/modals/EcommerceModal"));
registerModal("ecommercesync", () => import("../features/modals/EcommerceSyncModal"));
registerModal("addproduct", () => import("../features/modals/AddProductModal"));
registerModal("addproduct_images", () => import("../features/modals/AddProductImagesModal"));
registerModal("pricing", () => import("../features/modals/PricingModal"));

// Download modals
registerModal("download", () => import("../features/modals/DownloadModal"));
registerModal("downloads", () => import("../features/modals/DownloadsModal"));

// Newsletter modals
registerModal("editnewsletter", () => import("../features/modals/EditNewsletterModal"));
registerModal(
  "editnewsletter-artist",
  () => import("../features/modals/EditNewsletterArtistModal")
);

// Artist modals
registerModal("artistdetails", () => import("../features/modals/ArtistDetailsModal"));
registerModal("earnings", () => import("../features/modals/EarningsModal"));
registerModal("directory", () => import("../features/modals/DirectoryModal"));
registerModal("directoryedit", () => import("../features/modals/DirectoryEditModal"));

// Partner modals
registerModal("partnerdetails", () => import("../features/modals/ResellerDetailsModal"));
registerModal("manageinventory", () => import("../features/modals/ManageInventoryModal"));
registerModal("importinventory", () => import("../features/modals/ImportInventoryIdsModal"));

// Metadata modals
registerModal("multimeta", () => import("../features/modals/MultiMetaModal"));
registerModal("metadatascreening", () => import("../features/modals/MetadataScreeningModal"));

// Portfolio & settings
registerModal("settings", () => import("../features/modals/SettingsModal"));

// ── Data-driven modals (batch 7) ──────────────────────────────────

// Artist group management
registerModal("artistgroups", () => import("../features/modals/ArtistGroupsModal"));
registerModal("addtoartistgroups", () => import("../features/modals/AddToArtistGroupsModal"));
registerModal(
  "selectartistsforgroup",
  () => import("../features/modals/SelectArtistsForGroupModal")
);
registerModal("mats", () => import("../features/modals/MatsModal"));
